import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CryptoService } from './crypto.service';
import { StorageKey } from '../classes/storage.model';

export type StorageKeyType = typeof StorageKey[keyof typeof StorageKey];

@Injectable({ providedIn: 'root' })
export class AppStorageService {
	private subjects = new Map<
		StorageKeyType,
		BehaviorSubject<any>
	>();

	constructor(private crypto: CryptoService) {
		window.addEventListener(
			'storage',
			this.onStorageChange.bind(this)
		);
	}
	private readStorage<T>(key: StorageKeyType): T | null {
		const encrypted = localStorage.getItem(key);
		if (!encrypted) {
			return null;
		}
		try {
			const decrypted = this.crypto.get(encrypted);
			return JSON.parse(decrypted) as T;

		} catch {
			localStorage.removeItem(key);
			return null;
		}
	}

	private writeStorage<T>(key: StorageKeyType, value: T): void {
		const encrypted = this.crypto.set(JSON.stringify(value));
		localStorage.setItem(key, encrypted);
	}

	select<T>(key: StorageKeyType): Observable<T | null> {
		if (!this.subjects.has(key)) {
			const initial = this.readStorage<T>(key);
			this.subjects.set(key, new BehaviorSubject<T | null>(initial));
		}
		return this.subjects.get(key)!.asObservable();
	}

	get<T>(key: StorageKeyType): T | null {
		if (this.subjects.has(key)) {
			return this.subjects.get(key)!.getValue();
		}
		return this.readStorage<T>(key);
	}
	set<T>(key: StorageKeyType, value: T): void {
		// console.log(`AppStorageService: set key=${key}, value=`, value);
		this.writeStorage(key, value);
		this.ensureSubject(key);
		this.subjects.get(key)!.next(value);
	}
	remove(key: StorageKeyType): void {
		localStorage.removeItem(key);
		this.ensureSubject(key);
		this.subjects.get(key)!.next(null);
	}

	private onStorageChange(event: StorageEvent): void {
		if (!event.key) {
			return;
		}
		// console.log(`AppStorageService: onStorageChange key=${event.key}, newValue=`, event.newValue);
		const key = event.key as StorageKeyType;
		if (!Object.values(StorageKey).includes(key)) {
			return;
		}
		this.ensureSubject(key);
		const value = this.readStorage(key);

		this.subjects.get(key)!.next(value);
	}

	private ensureSubject(key: StorageKeyType): void {
		if (this.subjects.has(key)) {
			return;
		}
		// console.log(`AppStorageService: ensureSubject key=${key}`);
		this.subjects.set(key, new BehaviorSubject(this.readStorage(key)));
	}
}

